import { useRef, useState } from "react";

import { isEqual, pick } from "lodash";

import { productAnalytics, useTranslation } from "utils";

import { SCENARIO_LIMITS } from "consts";

import { CropDetailsDrawerTarget, CropYear, InvalidYearIdsByScenario, ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import {
  buildLocalScenario,
  getIncompleteYearIds,
  withAddedYear,
  withAppliedYearToAllMatchingCrops,
  withRemovedOrClearedYear,
  withRemovedScenario,
  withReorderedYears,
  withSavedYear,
  withUpdatedScenarioTitle,
  withUpdatedYearData,
} from "pages/app/scenario-analysis/utils/scenarioBuilderState";
import type { AutosavedChangeType } from "pages/app/scenario-analysis/utils/soilSightTracking";

type AddYearIssue = { scenarioId: string; yearIds: string[] } | null;

type ScenarioBuilderHandlersArgs = {
  scenarios: ScenarioData[];
  setScenarios: (updater: ScenarioData[] | ((prev: ScenarioData[]) => ScenarioData[])) => void;
  onCreateScenario?: (title: string, templateScenarioId?: string) => Promise<void> | void;
  onPersistScenarioYears?: (scenarioId: string, years: CropYear[]) => Promise<boolean> | void;
  /** We tag the edit here and report it when its autosave lands. */
  onScenarioChange?: (scenarioId: string, changeType: AutosavedChangeType) => void;
  onClearValidationErrors?: () => void;
  recalcValidationErrors?: InvalidYearIdsByScenario;
};

/** A year that got a crop or was removed no longer blocks adding a year. */
const withoutAddYearIssueYear = (issue: AddYearIssue, scenarioId: string, yearId: string): AddYearIssue => {
  if (issue?.scenarioId !== scenarioId) return issue;
  const nextIds = issue.yearIds.filter((id) => id !== yearId);
  return nextIds.length > 0 ? { ...issue, yearIds: nextIds } : null;
};

/** Edits to the scenario cards and the crop details drawer, plus the per-scenario validation feedback. */
export const useScenarioBuilderHandlers = ({
  scenarios,
  setScenarios,
  onCreateScenario,
  onPersistScenarioYears,
  onScenarioChange,
  onClearValidationErrors,
  recalcValidationErrors,
}: ScenarioBuilderHandlersArgs) => {
  const { t } = useTranslation("scenarioAnalysis");
  const addYearValidationMessage = t("scenarioBuilder.addYearValidationMessage");
  const recalcValidationMessage = t("scenarioBuilder.recalcValidationMessage");

  // Handlers read the latest scenarios from a ref, since React may run setScenarios updaters twice.
  const scenariosRef = useRef<ScenarioData[]>(scenarios);
  scenariosRef.current = scenarios;

  const [addYearIssue, setAddYearIssue] = useState<AddYearIssue>(null);
  const [drawerState, setDrawerState] = useState<CropDetailsDrawerTarget | null>(null);

  const handleTitleChange = (scenarioId: string, newTitle: string) => {
    onScenarioChange?.(scenarioId, "rename");
    setScenarios((prev) => withUpdatedScenarioTitle(prev, scenarioId, newTitle));
  };

  const handleDelete = (scenarioId: string) => {
    setScenarios((prev) => withRemovedScenario(prev, scenarioId));
  };

  const handleYearDataChange = (scenarioId: string, yearId: string, updates: Partial<CropYear>) => {
    const year = scenariosRef.current.find((scenario) => scenario.id === scenarioId)?.years.find((cropYear) => cropYear.id === yearId);
    // A no-op skips the autosave, so its mark would be reported with the next unrelated save.
    if (!isEqual(pick(year, Object.keys(updates)), updates)) onScenarioChange?.(scenarioId, "rotation");
    setScenarios((prev) => withUpdatedYearData(prev, scenarioId, yearId, updates));

    if ("crop" in updates && updates.crop) {
      setAddYearIssue((prev) => withoutAddYearIssueYear(prev, scenarioId, yearId));
    }
  };

  const handleYearRemove = (scenarioId: string, yearId: string) => {
    onScenarioChange?.(scenarioId, "rotation");
    onClearValidationErrors?.();
    setScenarios((prev) => withRemovedOrClearedYear(prev, scenarioId, yearId));
    setAddYearIssue((prev) => withoutAddYearIssueYear(prev, scenarioId, yearId));
  };

  const handleYearReorder = (scenarioId: string, fromIndex: number, toIndex: number) => {
    onScenarioChange?.(scenarioId, "rotation");
    setScenarios((prev) => withReorderedYears(prev, scenarioId, fromIndex, toIndex));
  };

  const openCropDetails = (target: CropDetailsDrawerTarget) => setDrawerState(target);

  const closeCropDetails = () => setDrawerState(null);

  const handleRefine = (scenarioId: string, yearId: string) => {
    openCropDetails({ scenarioId, yearId });
  };

  // Fire-and-forget: parent owns the toast/retry semantics.
  const persistYearsForScenario = (scenarioId: string, updatedYears: CropYear[]): Promise<boolean> =>
    onPersistScenarioYears ? Promise.resolve(onPersistScenarioYears(scenarioId, updatedYears)).then(Boolean, () => false) : Promise.resolve(false);

  const handleSaveForYear = (updatedYear: CropYear) => {
    if (!drawerState) return;
    const { scenarioId, yearId } = drawerState;
    const next = withSavedYear(scenariosRef.current, scenarioId, yearId, updatedYear);
    setScenarios(next.scenarios);
    closeCropDetails();
    if (next.years.length === 0) return;
    persistYearsForScenario(scenarioId, next.years).then((saved) => {
      if (saved) productAnalytics.track("soilsights:scenario_update", { change_type: "crop_details" });
    });
  };

  const handleApplyToAll = (updatedYear: CropYear) => {
    if (!drawerState) return;
    const { scenarioId, yearId } = drawerState;
    const next = withAppliedYearToAllMatchingCrops(scenariosRef.current, scenarioId, yearId, updatedYear);
    setScenarios(next.scenarios);
    closeCropDetails();
    Promise.all(next.affectedScenarios.map(({ id, years }) => persistYearsForScenario(id, years))).then((saved) => {
      if (saved.some(Boolean)) productAnalytics.track("soilsights:scenario_update", { change_type: "apply_to_all" });
    });
  };

  const handleAddYear = (scenarioId: string) => {
    const scenario = scenarios.find((s) => s.id === scenarioId);
    if (!scenario || scenario.years.length >= SCENARIO_LIMITS.MAX_YEARS) return;

    const incompleteYearIds = getIncompleteYearIds(scenarios, scenarioId);
    if (incompleteYearIds.length > 0) {
      setAddYearIssue({ scenarioId, yearIds: incompleteYearIds });
      return;
    }

    const newYearId = `${scenarioId}-y-${Date.now()}`;
    onScenarioChange?.(scenarioId, "rotation");
    setAddYearIssue(null);
    onClearValidationErrors?.();
    setScenarios((prev) => withAddedYear(prev, scenarioId, newYearId));
  };

  const handleAddScenario = (title: string, copyFromScenarioId?: string) => {
    // A parent creator POSTs and appends the server-issued scenario; without one, add it locally.
    if (onCreateScenario) {
      // Fire-and-forget: parent owns the toast/retry.
      Promise.resolve(onCreateScenario(title, copyFromScenarioId)).catch(() => {});
      return;
    }

    setScenarios((prev) => {
      const newScenario = buildLocalScenario(prev, title, copyFromScenarioId);
      return [...prev, newScenario];
    });
  };

  const getInvalidYearIdsForScenario = (scenarioId: string): string[] => {
    const addYear = addYearIssue?.scenarioId === scenarioId ? addYearIssue.yearIds : [];
    const recalcCrop = recalcValidationErrors?.get(scenarioId)?.cropYearIds ?? [];
    return [...new Set([...addYear, ...recalcCrop])];
  };

  const getAddYearErrorMessage = (scenarioId: string): string | undefined =>
    addYearIssue?.scenarioId === scenarioId && addYearIssue.yearIds.length > 0 ? addYearValidationMessage : undefined;

  const getValidationErrorMessage = (scenarioId: string): string | undefined => (recalcValidationErrors?.has(scenarioId) ? recalcValidationMessage : undefined);

  return {
    drawerState,
    openCropDetails,
    closeCropDetails,
    handleTitleChange,
    handleDelete,
    handleYearDataChange,
    handleYearRemove,
    handleYearReorder,
    handleRefine,
    handleSaveForYear,
    handleApplyToAll,
    handleAddYear,
    handleAddScenario,
    getInvalidYearIdsForScenario,
    getAddYearErrorMessage,
    getValidationErrorMessage,
  };
};
